/**
 * Referral link capture.
 *
 * A referral link (`/login?ref=CODE`) is opened before the user has an account.
 * Sign-up then leaves the SPA entirely for the Entra redirect flow, so the code
 * has to survive a full page teardown to reach the registration request. It is
 * parked in localStorage rather than sessionStorage because people click an
 * invite and finish signing up later, sometimes in a different tab.
 */

const STORAGE_KEY = "tempii.referral-code";

/** Query parameter on an invite link. */
export const REFERRAL_QUERY_PARAM = "ref";

/** Mirrors Tempii.Application.Abstractions.Data.ReferralCodeGenerator. */
const CODE_LENGTH = 8;
const CODE_PATTERN = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/;

/**
 * A parked code goes stale: it should not silently attach itself to a sign-up
 * weeks after the link was opened and forgotten.
 */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

type StoredReferral = {
  code: string;
  capturedAt: number;
};

export function normalizeReferralCode(code: string): string {
  return code.trim().toUpperCase();
}

export function isValidReferralCode(code: string): boolean {
  return CODE_PATTERN.test(code);
}

export { CODE_LENGTH as REFERRAL_CODE_LENGTH };

/**
 * Reads `?ref=` off a URL search string and parks it. Returns the stored code,
 * or null when the parameter is absent or malformed.
 */
export function captureReferralCodeFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get(REFERRAL_QUERY_PARAM);
  if (!raw) {
    return null;
  }

  const code = normalizeReferralCode(raw);
  if (!isValidReferralCode(code)) {
    return null;
  }

  storeReferralCode(code);
  return code;
}

export function storeReferralCode(code: string): void {
  const entry: StoredReferral = { code, capturedAt: Date.now() };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* private browsing or a full quota — the code is simply not carried over */
  }
}

/** Returns the parked code, or null if it is missing, malformed, or expired. */
export function readStoredReferralCode(): string | null {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }

  if (!raw) {
    return null;
  }

  try {
    const entry = JSON.parse(raw) as Partial<StoredReferral>;
    if (
      typeof entry.code !== "string" ||
      typeof entry.capturedAt !== "number" ||
      !isValidReferralCode(entry.code)
    ) {
      clearStoredReferralCode();
      return null;
    }

    if (Date.now() - entry.capturedAt > TTL_MS) {
      clearStoredReferralCode();
      return null;
    }

    return entry.code;
  } catch {
    clearStoredReferralCode();
    return null;
  }
}

export function clearStoredReferralCode(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/** Builds the shareable invite link for a referral code. */
export function buildReferralLink(code: string, origin?: string): string {
  const base = origin ?? window.location.origin;
  return `${base}/login?${REFERRAL_QUERY_PARAM}=${encodeURIComponent(code)}`;
}
